/**
 * Character editor and portrait upload.
 *
 * Two different things in one panel, because to the person using it they are
 * both "how I look": the generated character that walks around the map, and
 * the uploaded photo that appears beside their name in the roster and chat.
 *
 * Edits preview instantly and are sent as they are made — there is no Save
 * button, because every change is one small message and undoing is just
 * clicking the previous swatch.
 */

import {
  APPEARANCE_SIZES,
  AVATAR_PIXELS,
  CLOTHING_COLORS,
  HAIR_COLORS,
  HAIR_STYLES,
  SKIN_TONES,
  randomAppearance,
  type Appearance,
} from '@virtual-office/shared';

import { removeAvatar, uploadAvatar } from '../api.js';
import { characterSprite } from '../sprite.js';
import type { Store } from '../state.js';
import { $, clear, el } from './dom.js';
import { toast } from './toasts.js';

export class AppearancePanel {
  private readonly panel = $('appearance');
  private readonly preview = $('appearance-preview');
  private readonly options = $('appearance-options');
  private readonly portrait = $('appearance-portrait');
  private readonly fileInput = $<HTMLInputElement>('avatar-file');

  private look: Appearance = { skin: 0, hair: 1, hairColor: 0, shirt: 0, pants: 9 };

  constructor(
    private readonly store: Store,
    private readonly onChange: (look: Appearance) => void,
  ) {
    $('appearance-close').addEventListener('click', () => this.close());

    $('avatar-pick').addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', () => void this.handleFile());
    $('avatar-remove').addEventListener('click', () => void this.handleRemove());

    $('appearance-random').addEventListener('click', () => {
      this.look = randomAppearance();
      this.commit();
    });

    this.store.on('welcome', () => {
      const me = this.store.me;
      if (me) this.look = { ...me.appearance };
      if (this.isOpen) this.render();
    });
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    const me = this.store.me;
    if (me) this.look = { ...me.appearance };
    this.panel.hidden = false;
    this.render();
  }

  close(): void {
    this.panel.hidden = true;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private commit(): void {
    // Apply locally first so the map updates on the same frame as the panel.
    const me = this.store.me;
    if (me) me.appearance = { ...this.look };
    this.onChange({ ...this.look });
    this.render();
  }

  /* --- character ---------------------------------------------------------- */

  private render(): void {
    // Four facings, so you can see the character from every side at once.
    clear(this.preview);
    for (const facing of ['down', 'left', 'right', 'up'] as const) {
      const sprite = characterSprite(this.look, facing, 0, 4);
      const holder = el('div', { className: 'preview-cell' });
      holder.append(sprite);
      this.preview.append(holder);
    }

    clear(this.options);
    this.swatchRow('Skin', SKIN_TONES, 'skin');
    this.styleRow('Hair', HAIR_STYLES, 'hair');
    this.swatchRow('Hair colour', HAIR_COLORS, 'hairColor');
    this.swatchRow('Shirt', CLOTHING_COLORS, 'shirt');
    this.swatchRow('Trousers', CLOTHING_COLORS, 'pants');

    this.renderPortrait();
  }

  private swatchRow(label: string, colors: readonly string[], key: keyof Appearance): void {
    const row = el('div', { className: 'swatches' });
    colors.forEach((color, index) => {
      const button = el('button', { className: 'swatch small-swatch', title: color });
      button.type = 'button';
      button.style.background = color;
      button.setAttribute('aria-label', `${label} ${index + 1}`);
      if (this.look[key] === index) button.classList.add('selected');
      button.addEventListener('click', () => {
        this.look = { ...this.look, [key]: index };
        this.commit();
      });
      row.append(button);
    });
    this.options.append(el('div', { className: 'option-label', text: label }), row);
  }

  private styleRow(label: string, styles: readonly string[], key: keyof Appearance): void {
    const row = el('div', { className: 'style-row' });
    styles.forEach((style, index) => {
      const button = el('button', { className: 'style-chip', title: style });
      button.type = 'button';
      // The chip is the character wearing it — easier to choose from than a
      // list of words like "bob" and "messy".
      const thumb = characterSprite({ ...this.look, [key]: index }, 'down', 0, 2);
      button.append(thumb);
      if (this.look[key] === index) button.classList.add('selected');
      button.addEventListener('click', () => {
        this.look = { ...this.look, [key]: index };
        this.commit();
      });
      row.append(button);
    });
    this.options.append(el('div', { className: 'option-label', text: label }), row);
  }

  /* --- portrait ----------------------------------------------------------- */

  private renderPortrait(): void {
    clear(this.portrait);
    const me = this.store.me;
    if (me && me.avatarVersion > 0) {
      const img = el('img', { className: 'portrait-preview' });
      img.src = `/avatars/${me.id}.png?v=${me.avatarVersion}`;
      img.alt = 'Your portrait';
      this.portrait.append(img);
      $('avatar-remove').hidden = false;
    } else {
      this.portrait.append(
        el('p', {
          className: 'hint-inline',
          text: 'No photo yet — your character is used instead.',
        }),
      );
      $('avatar-remove').hidden = true;
    }
  }

  /**
   * Resize in the browser before uploading.
   *
   * The image is already decoded here, so cropping and scaling costs nothing —
   * and it means the server never needs an image library, which is what keeps
   * it free of native dependencies. A 4MB phone photo leaves as a ~20kB PNG.
   */
  private async handleFile(): Promise<void> {
    const file = this.fileInput.files?.[0];
    this.fileInput.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast('That is not an image.', 'error');
      return;
    }

    try {
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement('canvas');
      canvas.width = AVATAR_PIXELS;
      canvas.height = AVATAR_PIXELS;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not prepare the image.');

      // Centre crop to a square, so portraits are consistent wherever shown.
      const side = Math.min(bitmap.width, bitmap.height);
      ctx.drawImage(
        bitmap,
        (bitmap.width - side) / 2,
        (bitmap.height - side) / 2,
        side,
        side,
        0,
        0,
        AVATAR_PIXELS,
        AVATAR_PIXELS,
      );
      bitmap.close();

      const dataUrl = canvas.toDataURL('image/png');
      await uploadAvatar(dataUrl);
      toast('Portrait updated');
      this.renderPortrait();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not upload that.', 'error');
    }
  }

  private async handleRemove(): Promise<void> {
    try {
      await removeAvatar();
      toast('Portrait removed');
      this.renderPortrait();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not remove it.', 'error');
    }
  }
}

/** Palette sizes, re-exported so callers do not import constants twice. */
export const appearanceSizes = APPEARANCE_SIZES;
